import { useFieldApi, useFieldState } from 'informed';
import React, { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { days, getYears, months } from './helpers';
import { Select } from './Select';
import { capitalize } from '@/helpers/common';
import { ThemeMode } from '@/helpers/enums';
import { Validator } from '@/helpers/validators';
import { useCommonStore } from '@/store';

import { ScInputStructure } from './structures/styled';

interface InputDateProps {
  name: string;
  disabled?: boolean;
  initialValue?: string;
  validate?: Validator<any, any>;
}

export const InputDate: FC<InputDateProps> = ({
  name,
  disabled,
  initialValue,
  validate,
}) => {
  const { t } = useTranslation();

  const setError = () => 'dateError';

  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const countryCode = useCommonStore.use.countryCode();

  const [initialValueYear, initialValueMonth, initialValueDay] = useMemo(() => {
    if (!initialValue) return [];
    return initialValue.split('-');
  }, [initialValue]);

  const { day, month, year } = {
    day: `${name}.day`,
    month: `${name}.month`,
    year: `${name}.year`,
  };

  const stateDay = useFieldState(day);
  const stateMonth = useFieldState(month);
  const stateYear = useFieldState(year);
  const fieldStatedApi = useFieldApi(day);
  const fieldMonthApi = useFieldApi(month);
  const fieldYearApi = useFieldApi(year);

  const date = useMemo(() => {
    if (!stateDay || !stateMonth || !stateYear) return;

    if (!stateDay.value || !stateMonth.value || !stateYear.value) {
      return;
    }

    return `${stateDay.value}/${stateMonth.value}/${stateYear.value}`;
  }, [stateDay, stateMonth, stateYear]);

  const errorField = useMemo(() => {
    if (validate == null) return;

    if (!stateDay || !stateMonth || !stateYear) return;

    if (
      (!stateDay.touched && !stateDay.value) ||
      (!stateMonth.touched && !stateMonth.value) ||
      (!stateYear.touched && !stateYear.value)
    ) {
      return;
    }

    return validate(date, undefined) as string;
  }, [date, stateDay, stateMonth, stateYear, validate]);

  const monthsWithTrans = useMemo(() => {
    return months.map(({ name, ...rest }) => ({
      ...rest,
      name: capitalize(t(`common:month.${name - 1}`)),
    }));
  }, [t]);

  const years = getYears(countryCode);

  const dateValidation = useMemo(() => {
    return errorField ? setError : undefined;
  }, [errorField]);

  useEffect(() => {
    fieldStatedApi.validate();
    fieldMonthApi.validate();
    fieldYearApi.validate();
  }, [errorField, fieldMonthApi, fieldStatedApi, fieldYearApi]);

  return (
    <ScInputStructure>
      <ScInputDayOfBirthday>
        <Select
          initialValue={initialValueDay}
          name={`${name}.day`}
          placeholder={t('common:date.day')}
          options={days}
          validate={dateValidation}
          hideInputErrorColor
          hideErrorMessage
          validateOn="blur"
          disabled={disabled}
          native={isMobileDevice}
          mode={ThemeMode.Light}
        />
        <Select
          initialValue={initialValueMonth}
          name={`${name}.month`}
          placeholder={t('common:date.month')}
          options={monthsWithTrans}
          validate={dateValidation}
          hideInputErrorColor
          hideErrorMessage
          validateOn="blur"
          disabled={disabled}
          native={isMobileDevice}
          mode={ThemeMode.Light}
        />
        <Select
          initialValue={initialValueYear}
          name={`${name}.year`}
          placeholder={t('common:date.year')}
          options={years}
          validate={dateValidation}
          hideInputErrorColor
          hideErrorMessage
          validateOn="blur"
          disabled={disabled}
          native={isMobileDevice}
          mode={ThemeMode.Light}
        />
        {errorField && <div className="error">{t(errorField as string)}</div>}
      </ScInputDayOfBirthday>
    </ScInputStructure>
  );
};

const ScInputDayOfBirthday = styled.div`
  display: grid;
  grid-template-columns: 0.7fr repeat(2, 1fr);
  grid-column-gap: 8px;
  grid-row-gap: 8px;

  & > div {
    width: 100%;

    &.error {
      grid-column: 1 / 4;
    }
  }
`;
